import { readConfig } from './config';

interface JwtResponse {
  jwt: string;
  expiresAt: string; // ISO 8601 UTC
}

let cached: { jwt: string; expiresAtMs: number } | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inflight: Promise<string | null> | null = null;

/**
 * Returns a valid JWT for the current cookie-authenticated user.
 * - Returns null if the user is not authenticated (data-is-authenticated="false").
 * - Caches the JWT and auto-refreshes 2 minutes before expiry.
 * - Deduplicates concurrent calls: two simultaneous getJwt() calls
 *   result in exactly one /api/me/jwt request.
 */
export async function getJwt(): Promise<string | null> {
  if (!readConfig().isAuthenticated) {
    return null;
  }

  const now = Date.now();
  if (cached !== null && cached.expiresAtMs > now + 120_000) {
    return cached.jwt;
  }

  if (inflight !== null) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const resp = await fetch('/api/me/jwt', { credentials: 'include' });
      if (!resp.ok) {
        cached = null;
        return null;
      }
      const data = (await resp.json()) as JwtResponse;
      const expiresAtMs = Date.parse(data.expiresAt);
      cached = { jwt: data.jwt, expiresAtMs };
      scheduleRefresh(expiresAtMs);
      return data.jwt;
    } catch {
      cached = null;
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

function scheduleRefresh(expiresAtMs: number): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
  }
  const refreshInMs = expiresAtMs - Date.now() - 120_000;
  if (refreshInMs <= 0) {
    return;
  }
  refreshTimer = setTimeout(() => {
    void getJwt();
  }, refreshInMs);
}

/**
 * Clears the JWT cache and cancels any pending auto-refresh timer.
 * Call on logout.
 */
export function clearJwtCache(): void {
  cached = null;
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}
